//=================================================
// Image viewers (OpenSeadragon)
//
// The image viewer behaves slightly differently whether it's inside a lightbox
// or if it's 'inline' on an artwork page. Mainly, the inline view can expand
// vertically when zooming and the image is left justified inside the canvas.
//=================================================

import { createInitializeOnce } from './utilities';

const initializeOnce = createInitializeOnce();

// Ratio for how much bigger the image needs to be than the canvas to allow zooming
const zoomRatio = 1.25;
const zoomVisibilityRatio = 0.75;

function shouldLoadViewer(img) {
    // Briefly make sure image is laid out for sizing calculations
    Object.assign(img.style, {
        display: 'flex',
        visibility: 'hidden',
        position: 'absolute',
    });

    // If either width or height are at least zoomRatio bigger than the container load the image viewer
    let loadViewer = (img.naturalWidth >= img.clientWidth * zoomRatio) || (img.naturalHeight >= img.clientHeight * zoomRatio);

    // Unset temporary layout
    Object.assign(img.style, {
        display: '',
        visibility: '',
        position: '',
    });

    return loadViewer;
}

// Aspect ratio for the canvas, which is what gives the viewer its height
// Never taller than 1:1 ratio
function canvasRatio(img) {
    const aspectRatio = img.naturalWidth / img.naturalHeight;

    return aspectRatio > 1.0 ? 1.0 : aspectRatio;
}

// Calculate the size of the right margin required to align the image to the left of the canvas.
function calculateRightMargin(root, canvas) {
    const maxRatio = parseFloat(getComputedStyle(root).getPropertyValue('--image-viewer-ratio')) || 1;
    const imageRatio = parseFloat(getComputedStyle(root).getPropertyValue('--artwork-image-ratio')) || maxRatio;

    if (imageRatio >= maxRatio) {
        return 0;
    }

    // Deliberately not rounded: a whole-pixel margin leaves the usable viewport a fraction wider
    // than the image's ratio, so the fit comes out just under 1 and the artwork lands very slightly
    // scaled down. The exact value makes home zoom land on 1.
    const margin = canvas.getBoundingClientRect().width * (1 - (imageRatio / maxRatio));

    // An artwork whose ratio lands a hair under the cap — a nominal 3:2 that measures 1.49925, say —
    // comes out short of the full width by a fraction of a pixel. There's nothing there to align, so
    // don't reserve it: a margin nobody can see is a margin that only makes the viewport disagree
    // with what's on screen.
    return margin < 1 ? 0 : margin;
}

function isWideImage(root) {
    const style = getComputedStyle(root);

    return parseFloat(style.getPropertyValue('--artwork-image-ratio')) >
        parseFloat(style.getPropertyValue('--image-viewer-expand-ratio'));
}

// OpenSeadragon reports tiny zoom differences, so only treat a real change as "zoomed in"
function isZoomed(viewer) {
    return viewer.viewport.getZoom() > viewer.viewport.getHomeZoom() * 1.001;
}

function isPanned(viewer) {
    const viewport = viewer.viewport;
    const offset = viewport.getCenter().minus(viewport.getHomeBounds().getCenter());
    const pixels = viewport.deltaPixelsFromPoints(offset);

    return Math.abs(pixels.x) > 1 || Math.abs(pixels.y) > 1;
}

function adjustImageViewer(root, OpenSeadragon, inline = false) {
    const viewerEl = root.classList.contains('image-viewer') ? root : root.querySelector('.image-viewer');
    const canvas = root.querySelector('.image-viewer__canvas');
    const img = root.querySelector('.image-viewer__image');
    // Both viewers render the same toolbar partial, so these are found the same way in either
    const homeEl = root.querySelector('.image-viewer-toolbar__home');
    const zoomInEl = root.querySelector('.image-viewer-toolbar__zoom-in');
    const zoomOutEl = root.querySelector('.image-viewer-toolbar__zoom-out');
    let loadViewer = true;

    if (!viewerEl || !canvas || !img) {
        return;
    }

    // Wide images on collection page should expand vertically when zooming to increase the
    // otherwise very small canvas.
    const expandable = inline && isWideImage(root);

    function isExpanded() {
        return viewerEl.classList.contains('image-viewer--expanded');
    }

    // OpenSeadragon stamps title="Go home" on whatever element it's handed as the home button.
    // Our buttons carry an aria-label so the `title` and its hover state are not necessary.
    OpenSeadragon.setString('Tooltips.Home', '');

    function setState() {
        viewerEl.classList.toggle('active', loadViewer);
        // Also hides .image-viewer-toolbar for images that cannot be zoomed
        viewerEl.classList.toggle('static', !loadViewer);

        if (inline) {
            viewerEl.classList.toggle('image-viewer--expandable', expandable);
        }
    }

    // The in-page viewer is sized entirely in CSS; only the lightbox needs a ratio set here
    function setCanvasSize() {
        if (!inline) {
            canvas.style.aspectRatio = `${ canvasRatio(img) }`;
        }
    }

    // Keeps the image against the left edge instead of centred in the full-width viewer
    function alignImageLeft(viewer) {
        const margin = calculateRightMargin(root, canvas);

        if (viewer.viewport.getMargins().right === margin) {
            return;
        }

        // Margins shrink the usable viewport but leave the zoom level alone, so the image has to be
        // re-fitted to the space that's left
        viewer.viewport.setMargins({ right: margin });
        viewer.viewport.goHome(true);
    }

    function setSizing() {
        setCanvasSize();

        loadViewer = shouldLoadViewer(img);
        setState();
    }

    // Zoom towards the middle of the image (not the canvas)
    function zoomByStep(factor) {
        const viewer = OpenSeadragon.getViewer(canvas);
        if (!viewer) {
            return;
        }

        viewer.viewport.zoomBy(factor(viewer));
        viewer.viewport.applyConstraints();
    }

    // Custom zoom button click handlers
    function bindZoomButtons() {
        [[zoomInEl, (v) => v.zoomPerClick], [zoomOutEl, (v) => 1 / v.zoomPerClick]].forEach(([el, factor]) => {
            if (!el || el.dataset.zoomBound) {
                return;
            }

            el.dataset.zoomBound = 'true';
            el.addEventListener('click', () => zoomByStep(factor));
        });
    }

    function initializeViewer() {
        // Creation is deferred until the image loads, so another call (a window resize, say, or a
        // quick second thumbnail click) can land in between. Two viewers on one element both render.
        if (OpenSeadragon.getViewer(canvas)) {
            return;
        }

        const viewer = OpenSeadragon({
            element: canvas,
            tileSources: {
                type: 'image',
                url: img.src,
            },
            visibilityRatio: loadViewer ? zoomVisibilityRatio : 1.0,
            minZoomImageRatio: 1.0,
            panHorizontal: loadViewer,
            panVertical: loadViewer,
            drawer: 'canvas',
            maxZoomPixelRatio: 1.2,
            // Custom zoom button handlers with bindZoomButtons()
            showZoomControl: false,
            homeButton: homeEl,
            showFullPageControl: false,
            showRotationControl: false,
            defaultZoomLevel: 0,
            gestureSettingsMouse: { scrollToZoom: false },
        });

        // Copy alt text to canvas which has role img (there may be a better way to do this, unsure best handling for WCAG)
        canvas.setAttribute('aria-label', img.alt);

        // Allow scrolling past
        viewer.addHandler('canvas-scroll', function (e) {
            e.preventDefault = false;
        });

        bindZoomButtons();

        // Disable default OSD behavior that causes the page to jump when interacting with it
        const focusCanvas = viewer.canvas.focus.bind(viewer.canvas);
        viewer.canvas.focus = (options) => focusCanvas({ ...options, preventScroll: true });

        function updateHomeButton() {
            if (homeEl) homeEl.disabled = !isZoomed(viewer) && !isPanned(viewer);
        }

        viewer.addHandler('zoom', () => {
            updateHomeButton();

            // Vertically expand the canvas if image is wide (and canvas is therefore small)
            if (expandable && isZoomed(viewer) && !isExpanded()) {
                viewerEl.classList.add('image-viewer--expanded');
            }
        });

        viewer.addHandler('pan', updateHomeButton);

        if (inline) {
            viewer.addHandler('open', () => {
                alignImageLeft(viewer);
            });

            return;
        }

        // Awkward, but need to make sure that the viewer is reset to "home" when it gets resized/moved around, and this isn't super smooth with OSD for some reason
        viewer.addHandler('resize', function () {
            // Check if the viewer is already fully loaded
            if (viewer.world.getItemCount() > 0 && viewer.world.getItemAt(0).getFullyLoaded()) {
                // Requires a slight delay for some reason in this case (ugh)
                setTimeout(() => {
                    viewer.viewport.goHome(true);
                }, 10)
            } else {
                // Add a one-time listener for the 'tile-drawn' event if the viewer isn't fully loaded yet
                let tileDrawnHandler = function () {
                    viewer.viewport.goHome(true);
                    // Remove the handler to prevent repeated adjustments
                    viewer.removeHandler('tile-drawn', tileDrawnHandler);
                };
                viewer.addHandler('tile-drawn', tileDrawnHandler);
            }
        });
    }

    // If viewer doesn't exist yet, create it
    let viewer = OpenSeadragon.getViewer(canvas);
    if (!viewer) {
        // Make sure the image is loaded before handling sizing and starting up OSD
        if (img.complete) {
            setSizing();
            initializeViewer();
        } else {
            img.onload = () => {
                setSizing();
                initializeViewer();
            };
        }

    } else {
        // ...otherwise simply update the existing viewer
        loadViewer = shouldLoadViewer(img);

        // Enable or disable zooming ability
        viewer.viewport.visibilityRatio = loadViewer ? zoomVisibilityRatio : 1.0;
        viewer.panHorizontal = loadViewer;
        viewer.panVertical = loadViewer;

        setState();

        // The max container ratio changes at the mobile breakpoint, so re-derive the reserved width
        if (inline) {
            alignImageLeft(viewer);
        }
    }
}

// Swap other artwork images into the viewer
function selectArtworkImage(root, button) {
    const img = root.querySelector('.image-viewer__image');
    const canvas = root.querySelector('.image-viewer__canvas');
    const buttons = Array.from(root.querySelectorAll('.artwork-thumbnails__button'));
    const index = buttons.indexOf(button);

    if (!img || !canvas || index < 0) {
        return;
    }

    buttons.forEach((el) => {
        el.classList.toggle('active', el === button);
        el.setAttribute('aria-pressed', el === button);
    });

    // Every recto/verso label is rendered; only the selected image's shows
    root.querySelectorAll('.artwork-viewer__label').forEach((el, i) => {
        el.classList.toggle('active', i === index);
    });

    // CSS sizes the viewer from this, so it has to change with the image or the box keeps the
    // previous one's shape
    if (button.dataset.imageRatio) {
        root.style.setProperty('--artwork-image-ratio', button.dataset.imageRatio);
    }

    // Reset the expanded state and allow the script to calculate expandable for new image
    canvas.closest('.image-viewer')?.classList.remove('image-viewer--expandable', 'image-viewer--expanded');

    img.src = button.dataset.imageUrl;
    img.alt = button.dataset.imageAlt || '';

    // OpenSeadragon can't re-point at a new tile source in place, so rebuild it around the new image
    import('openseadragon').then((OpenSeadragon) => {
        OpenSeadragon.default.getViewer(canvas)?.destroy();
        adjustImageViewer(root, OpenSeadragon.default, true);
    }).catch((error) => {
        console.error('Failed to load OpenSeadragon', error);
    });
}

function setupArtworkViewer(root) {
    root.querySelectorAll('.artwork-thumbnails__button').forEach((button) => {
        button.addEventListener('click', () => {
            selectArtworkImage(root, button);
        });
    });
}

export function adjustImageViewers() {
    if (!document.querySelector('.image-viewer')) return;

    // Avoid loading OpenSeadragon on every page because it is really big
    import('openseadragon').then((OpenSeadragon) => {
        // Only update an open viewer (update later if these can be in more places)
        document.querySelectorAll('dialog[open] .image-viewer').forEach((el) => {
            adjustImageViewer(el, OpenSeadragon.default);
        });

        // In-page viewers are always visible, so they always need adjusting
        document.querySelectorAll('.artwork-viewer').forEach((el) => {
            adjustImageViewer(el, OpenSeadragon.default, true);
        });
    }).catch((error) => {
        console.error('Failed to load OpenSeadragon', error);

        // Show the plain images even if OSD fails
        document.querySelectorAll('.image-viewer').forEach((el) => el.classList.add('static'));
    });
}

export function setupImageViewers() {
    document.querySelectorAll('.artwork-viewer').forEach((el) => {
        if (initializeOnce(el)) {
            setupArtworkViewer(el);
        }
    });

    // Run once initially
    adjustImageViewers();
}
